/** The quick actions every value in the session drawer offers.
 *
 *  `/users/sessions` is a plain-param grid (`useGridFilters`), so its links
 *  are built with `sessionsHref({...})` rather than a `?q=` DSL. The date
 *  filters take full ISO instants — the same strings the toolbar's range
 *  picker writes — so "sessions created that day" is a bounded pair rather
 *  than a free-text chip.
 *
 *  Id spaces: `?user=` and `?booking=` are console pks (the booking is the
 *  Group_Tour_User the token was minted for — the same id `/bookings/<id>`
 *  serves). Every href comes from gridLinks; every action from `act`.
 */
import type { AccessScope } from '@/api/sessions';
import type { UserKind } from '@/api/users';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { bookingsHref, cond, sessionsHref, usersHref } from '@/utils/gridLinks';

/** `UserKind` spells the guest kind singular; the grid route is plural. */
export function userGridKind(kind: UserKind): 'system' | 'staff' | 'guests' {
  if (kind === 'staff') return 'staff';
  if (kind === 'system') return 'system';
  return 'guests';
}

/** The whole UTC day around an instant, as the grid's from/to params. */
function dayBounds(iso: string): { from: string; to: string } {
  const start = new Date(iso);
  start.setUTCHours(0, 0, 0, 0);
  const end = new Date(start.getTime() + 24 * 3_600_000 - 1000);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function userActions(user: { id: number; email: string; kind: UserKind }): ValueAction[] {
  return [
    act.open(`/users/${user.id}`, 'Open user'),
    act.open(`/users/${user.id}/sessions`, "User's sessions tab"),
    act.filter(sessionsHref({ user: user.id }), 'Sessions of this user'),
    act.mailto(user.email),
  ];
}

export function kindActions(kind: UserKind): ValueAction[] {
  const grid = userGridKind(kind);
  return [act.filter(usersHref(grid), `Every ${grid} account`)];
}

export function tenantActions(tenant: { id: number }): ValueAction[] {
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(sessionsHref({ tenant: tenant.id }), 'Sessions of this operator'),
  ];
}

/** No tenant = a platform/system token; the grid spells that `tenant=null`. */
export function systemTenantActions(): ValueAction[] {
  return [act.filter(sessionsHref({ tenant: 'null' }), 'Sessions with no tenant')];
}

export function scopeActions(scope: AccessScope | string): ValueAction[] {
  return [act.filter(sessionsHref({ access: scope }), `Sessions scoped "${scope}"`)];
}

export function authActions(auth: number, label: string): ValueAction[] {
  return [act.filter(sessionsHref({ auth }), `Sessions authenticated by ${label.toLowerCase()}`)];
}

export function createdActions(iso: string): ValueAction[] {
  const { from, to } = dayBounds(iso);
  return [
    act.filter(sessionsHref({ created_from: from, created_to: to }), 'Sessions created that day'),
  ];
}

export function expiresActions(iso: string): ValueAction[] {
  const { from, to } = dayBounds(iso);
  return [
    act.filter(sessionsHref({ expires_from: from, expires_to: to }), 'Sessions expiring that day'),
    act.filter(sessionsHref({ active: 'false' }), 'Expired sessions'),
  ];
}

/** The booking a `booking`-scope token was minted for (console pk). */
export function bookingActions(booking: { id: number; label?: string | null }): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/bookings/${booking.id}`, 'Open booking'),
    act.filter(sessionsHref({ booking: booking.id }), 'Sessions minted for this booking'),
    act.filter(bookingsHref([cond('gtu', String(booking.id))]), 'This booking in the grid'),
  ];
  if (booking.label) out.push(act.copy(booking.label, 'Copy booking label'));
  return out;
}

export function sessionIdActions(id: number): ValueAction[] {
  return [act.copy(String(id), 'Copy session id')];
}
